import { useEffect } from 'react';
import { StoreBuildModel } from '../../../../models/store-build';
import { EnvironmentModel } from '../../StoreBuilderDetailsView/environments';
import {
  areAllShoppersDeleted,
  initializeQueueLines,
  removeShopper,
} from '../queueLineOperations';
import { QueueLineModel, ShopperMesh, ThreeRefs } from '../types';

interface UseKeyboardInteractionsProps {
  build: StoreBuildModel | null;
  environment: EnvironmentModel | null;
  refs: ThreeRefs;
  isShiftPressed: boolean;
  setIsShiftPressed: React.Dispatch<React.SetStateAction<boolean>>;
  activeTextMesh: THREE.Mesh | null;
  setActiveTextMesh: React.Dispatch<React.SetStateAction<THREE.Mesh | null>>;
  textInputRef: React.RefObject<HTMLInputElement>;
  setQueueLines: React.Dispatch<React.SetStateAction<QueueLineModel[]>>;
  cleanupAllSelectionOverlays: () => void;
  isDebugMode?: boolean;
  setIsDebugMode?: React.Dispatch<React.SetStateAction<boolean>>;
  onTogglePerformancePanel?: () => void;
}

export const useKeyboardInteractions = ({
  build,
  environment,
  refs,
  isShiftPressed,
  setIsShiftPressed,
  activeTextMesh,
  setActiveTextMesh,
  textInputRef,
  setQueueLines,
  cleanupAllSelectionOverlays,
  isDebugMode,
  setIsDebugMode,
  onTogglePerformancePanel,
}: UseKeyboardInteractionsProps): void => {
  // Handle keyboard events
  useEffect(() => {
    if (!build || !environment) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.shiftKey && !isShiftPressed) {
        setIsShiftPressed(true);
      }

      if (e.key === 'd' && e.ctrlKey && setIsDebugMode) {
        e.preventDefault();
        setIsDebugMode((prev) => !prev);
        console.log('Debug mode toggled:', !isDebugMode);
      }

      if (e.key === 'p' && e.ctrlKey && onTogglePerformancePanel) {
        e.preventDefault();
        onTogglePerformancePanel();
        console.log('Performance panel toggled');
      }

      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        refs.selectedMeshes.current.length > 0 &&
        refs.scene.current
      ) {
        const hasShoppers = refs.selectedMeshes.current.some(
          (mesh) => mesh.userData.isQueueLineShopper
        );

        if (hasShoppers) {
          setQueueLines((prevLines) => {
            const newLines = [...prevLines];

            refs.selectedMeshes.current.forEach((mesh) => {
              if (mesh.userData.isQueueLineShopper) {
                const shopper = mesh as ShopperMesh;
                removeShopper(refs.scene.current!, shopper, newLines);
              }
            });

            if (areAllShoppersDeleted(refs.scene.current!)) {
              cleanupAllSelectionOverlays();
              const resetLines = initializeQueueLines();
              refs.queueLines.current = resetLines;
              return resetLines;
            }

            refs.queueLines.current = newLines;
            return newLines;
          });
        }

        // Remove selected meshes
        refs.selectedMeshes.current.forEach((mesh) => {
          if (refs.scene.current) {
            if (mesh.userData.hasOverlay) {
              const overlayUuid = mesh.userData.hasOverlay;
              const overlay = refs.scene.current.children.find(
                (obj) => obj.uuid === overlayUuid
              );
              if (overlay) {
                refs.scene.current.remove(overlay);
              }
            }

            if (
              mesh.userData.isQueueLineShopper &&
              mesh.userData.badgeContainer
            ) {
              refs.scene.current.remove(mesh.userData.badgeContainer);
            }

            refs.scene.current.remove(mesh);
          }
        });

        if (activeTextMesh) {
          setActiveTextMesh(null);
          if (textInputRef.current) {
            textInputRef.current.style.display = 'none';
          }
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.shiftKey && isShiftPressed) {
        setIsShiftPressed(false);
      }
    };

    const handleBlur = () => {
      setIsShiftPressed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [
    isShiftPressed,
    build,
    environment,
    refs.scene,
    refs.selectedMeshes,
    activeTextMesh,
    cleanupAllSelectionOverlays,
    setIsShiftPressed,
    setActiveTextMesh,
    textInputRef,
    setQueueLines,
    refs.queueLines,
    isDebugMode,
    setIsDebugMode,
    onTogglePerformancePanel,
  ]);
};
